import { useState } from "react";
import axios from "axios";
import { API } from "@/contexts/AuthContext";
import WebsitePublicationControls from "@/components/dashboard/WebsitePublicationControls";
import WebsiteRenewalAdmin from "@/components/dashboard/WebsiteRenewalAdmin";

export default function PrivateWebsiteReleases({ draftId, sourceVersion, hasUnsavedChanges, busy, setBusy }) {
  const [releases, setReleases] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [pending, setPending] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const locked = busy || !!pending;

  const run = async (action, task) => {
    if (locked) return;
    setPending(action); setBusy(true); setError(""); setNotice("");
    try { await task(); }
    catch (failure) {
      let detail = failure?.response?.data?.detail;
      if (failure?.response?.data instanceof Blob) {
        try { detail = JSON.parse(await failure.response.data.text()).detail; } catch { /* Use safe fallback. */ }
      }
      setError(action === "create" && failure?.response?.status === 409
        ? "The draft changed elsewhere. Reopen its latest version before storing a release. Your edits are still here."
        : typeof detail === "string" ? detail
        : "Could not complete the private release action. Your draft has been kept. Try again.");
    } finally { setPending(""); setBusy(false); }
  };

  const create = () => {
    if (hasUnsavedChanges || !sourceVersion) {
      setError("Save your changes before storing a private release."); return;
    }
    return run("create", async () => {
      const { data } = await axios.post(
        API + "/website-builder/drafts/" + encodeURIComponent(draftId) + "/releases",
        null, { params: { expected_updated_at: sourceVersion } },
      );
      setReleases((items) => [data, ...items.filter((item) => item.id !== data.id)]);
      setNotice("Private release stored. It has not been published.");
    });
  };

  const refresh = () => run("list", async () => {
    const { data } = await axios.get(
      API + "/website-builder/drafts/" + encodeURIComponent(draftId) + "/releases",
    );
    setReleases(data.releases || []); setNextCursor(data.next_cursor || null); setLoaded(true);
  });

  const loadOlder = () => {
    if (!nextCursor) return;
    return run("older-releases", async () => {
      const { data } = await axios.get(
        API + "/website-builder/drafts/" + encodeURIComponent(draftId) + "/releases",
        { params: { before_created_at: nextCursor.created_at, before_id: nextCursor.id } },
      );
      setReleases((items) => {
        const known = new Set(items.map((item) => item.id));
        return [...items, ...(data.releases || []).filter((item) => !known.has(item.id))];
      });
      setNextCursor(data.next_cursor || null);
    });
  };

  const download = (release) => run(release.id, async () => {
    const { data } = await axios.get(
      API + "/website-builder/releases/" + encodeURIComponent(release.id) + "/download",
      { responseType: "blob" },
    );
    const url = URL.createObjectURL(data);
    const link = document.createElement("a");
    try {
      link.href = url; link.download = "zynthoro-website-release.zip";
      document.body.appendChild(link); link.click();
    } finally {
      link.remove(); window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
    setNotice("Private release download started. Your editor has not changed.");
  });

  return (
    <div className="space-y-3 rounded-lg border p-3">
      <h3 className="text-sm font-semibold">Private website releases</h3>
      <p className="text-xs text-slate-500">Keep a fixed copy of a saved website version. Storing or downloading a release keeps it private. Publication is a separate confirmed action below.</p>
      <div className="flex flex-wrap gap-2">
        <button type="button" disabled={locked || hasUnsavedChanges || !sourceVersion} onClick={create}
          className="rounded border px-3 py-2 text-sm disabled:opacity-50">
          {pending === "create" ? "Storing release…" : "Store private release"}
        </button>
        <button type="button" disabled={locked} onClick={refresh}
          className="rounded border px-3 py-2 text-sm disabled:opacity-50">
          {pending === "list" ? "Loading releases…" : loaded ? "Refresh releases" : "Show saved releases"}
        </button>
      </div>
      {hasUnsavedChanges && <p className="text-xs text-slate-500">Save changes before storing a new release.</p>}
      {pending && <p role="status" className="text-sm text-slate-500">Working…</p>}
      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
      {notice && <p role="status" className="text-sm text-emerald-700">{notice}</p>}
      {loaded && releases.length === 0 && <p className="text-sm text-slate-500">No private releases saved yet.</p>}
      {releases.length > 0 && <ul className="space-y-2">
        {releases.map((release) => (
          <li key={release.id} className="flex flex-wrap items-center justify-between gap-2 rounded border p-2 text-sm">
            <div>
              <p>Private · {new Date(release.created_at).toLocaleString()}</p>
              <p className="text-xs text-slate-500">Source saved: {new Date(release.source_updated_at).toLocaleString()}</p>
            </div>
            <button type="button" disabled={locked} onClick={() => download(release)}
              className="rounded border px-3 py-1 disabled:opacity-50">
              {pending === release.id ? "Downloading…" : "Download"}
            </button>
          </li>
        ))}
      </ul>}
      {nextCursor && <button type="button" disabled={locked} onClick={loadOlder}
        className="rounded border px-3 py-2 text-sm disabled:opacity-50">
        {pending === "older-releases" ? "Loading older releases…" : "Load older releases"}
      </button>}
      <WebsitePublicationControls draftId={draftId} releases={releases} locked={locked}
        hasUnsavedChanges={hasUnsavedChanges} run={run} />
      <WebsiteRenewalAdmin locked={locked} run={run} />
    </div>
  );
}
